// Experimental WebMCP support — format may change while Google develops the standard.
// See https://developer.chrome.com/docs/ai/webmcp
"use client";

import { useEffect } from "react";
import { BOX_TIERS, FREQUENCY_OPTIONS } from "@/lib/appstle";
import type {
	WebMCPModelContext,
	WebMCPRegisterToolDefinition,
} from "@/types/webmcp";

const MAX_TOOL_LIST_LIMIT = 10;

function getModelContext(): WebMCPModelContext | undefined {
	if (typeof document !== "undefined" && document.modelContext) {
		return document.modelContext;
	}
	if (typeof navigator !== "undefined" && navigator.modelContext) {
		return navigator.modelContext;
	}
	return undefined;
}

function toTextResult(value: unknown): {
	content: Array<{ type: "text"; text: string }>;
} {
	return { content: [{ type: "text", text: JSON.stringify(value) }] };
}

function toErrorResult(message: string): {
	content: Array<{ type: "text"; text: string }>;
} {
	return { content: [{ type: "text", text: `Error: ${message}` }] };
}

async function fetchJson(path: string): Promise<unknown> {
	const response = await fetch(path, {
		headers: { Accept: "application/json" },
	});
	if (!response.ok) {
		throw new Error(`Request failed with status ${response.status}.`);
	}
	return (await response.json()) as unknown;
}

function validatedQuery(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const query = raw.trim();
	if (query.length === 0 || query.length > 100) return null;
	return query;
}

function validatedHandle(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const handle = raw.trim().toLowerCase();
	if (!/^[a-z0-9-]+$/.test(handle) || handle.length > 100) return null;
	return handle;
}

function validatedLimit(raw: unknown): number {
	const limit =
		typeof raw === "number" && Number.isFinite(raw)
			? Math.trunc(raw)
			: MAX_TOOL_LIST_LIMIT;
	return Math.min(Math.max(limit, 1), MAX_TOOL_LIST_LIMIT);
}

function buildTools(): WebMCPRegisterToolDefinition[] {
	const searchMeals: WebMCPRegisterToolDefinition = {
		name: "search_meals",
		description:
			"Search Ice Age Meals products and meals by name or keyword. Returns matching products with price and availability.",
		inputSchema: {
			type: "object",
			properties: {
				query: {
					type: "string",
					description: "Meal or product name to search for.",
				},
			},
			required: ["query"],
		},
		annotations: {
			readOnlyHint: true,
			untrustedContentHint: false,
			consequentialHint: false,
			debugging: false,
		},
		execute: async (args) => {
			const query = validatedQuery(args.query);
			if (!query) return toErrorResult("Provide a non-empty query.");
			try {
				const data = await fetchJson(
					`/api/webmcp/products?q=${encodeURIComponent(query)}`,
				);
				return toTextResult(data);
			} catch {
				return toErrorResult("Search is currently unavailable.");
			}
		},
	};

	const listProducts: WebMCPRegisterToolDefinition = {
		name: "list_products",
		description:
			"List Ice Age Meals products with optional category filter. Returns name, price, availability, and URL.",
		inputSchema: {
			type: "object",
			properties: {
				category: {
					type: "string",
					description: "Filter by product type, e.g. burrito.",
				},
				limit: {
					type: "number",
					description: "Max results, 1 to 10.",
				},
			},
			required: [],
		},
		annotations: {
			readOnlyHint: true,
			untrustedContentHint: false,
			consequentialHint: false,
			debugging: false,
		},
		execute: async (args) => {
			try {
				const params = new URLSearchParams();
				if (typeof args.category === "string" && args.category.trim()) {
					params.set("category", args.category.trim().slice(0, 100));
				}
				params.set("limit", String(validatedLimit(args.limit)));
				const data = await fetchJson(
					`/api/webmcp/products?${params.toString()}`,
				);
				return toTextResult(data);
			} catch {
				return toErrorResult("Product listing is currently unavailable.");
			}
		},
	};

	const getProduct: WebMCPRegisterToolDefinition = {
		name: "get_product",
		description:
			"Get public details for one product by handle: price, variants, availability, subscription availability, URL.",
		inputSchema: {
			type: "object",
			properties: {
				handle: {
					type: "string",
					description: "Product handle, e.g. fbb-box.",
				},
			},
			required: ["handle"],
		},
		annotations: {
			readOnlyHint: true,
			untrustedContentHint: false,
			consequentialHint: false,
			debugging: false,
		},
		execute: async (args) => {
			const handle = validatedHandle(args.handle);
			if (!handle) return toErrorResult("Provide a valid product handle.");
			try {
				const data = await fetchJson(
					`/api/webmcp/products?handle=${encodeURIComponent(handle)}`,
				);
				return toTextResult(data);
			} catch {
				return toErrorResult("Product lookup is currently unavailable.");
			}
		},
	};

	const getBuildABoxOptions: WebMCPRegisterToolDefinition = {
		name: "get_build_a_box_options",
		description:
			"List Build-a-Box sizes with subscription discounts and delivery frequencies. Discovery only.",
		inputSchema: {
			type: "object",
			properties: {},
			required: [],
		},
		annotations: {
			readOnlyHint: true,
			untrustedContentHint: false,
			consequentialHint: false,
			debugging: false,
		},
		execute: () => {
			return toTextResult({
				boxSizes: BOX_TIERS.map((tier) => ({
					meals: tier.count,
					subscriptionDiscountPercent: tier.subscriptionDiscount,
				})),
				frequencies: FREQUENCY_OPTIONS.map((option) => option.value),
				note: "Boxes must contain exactly the chosen number of meals. Checkout stays in the normal store flow.",
			});
		},
	};

	return [searchMeals, listProducts, getProduct, getBuildABoxOptions];
}

export function WebMCPProvider() {
	useEffect(() => {
		try {
			const modelContext = getModelContext();
			if (!modelContext || typeof modelContext.registerTool !== "function") {
				return;
			}
			for (const tool of buildTools()) {
				modelContext.registerTool(tool);
			}
		} catch {
			// WebMCP is strictly progressive enhancement; never break the page.
		}
	}, []);

	return null;
}
